/**
 * pz-localization-selector.js — механика селектора страны/языка
 * (дизайн-хендоф 2026-07-29): вкладки Country/Language, поиск по странам,
 * ⓘ-пояснение, открытие/закрытие панели.
 *
 * Два варианта одного элемента:
 *  - pill (десктоп): панель = absolute-выпадашка (hidden-атрибут),
 *    закрытие по клику мимо и Escape;
 *  - row (мобильное меню): панель = НАСТОЯЩИЙ <dialog> + showModal —
 *    top layer игнорирует transform/overflow .menu-drawer (иначе fixed
 *    якорился бы к боксу drawer); скрим = ::backdrop, Escape = нативный
 *    cancel. Escape ГЛУШИТСЯ (stopPropagation на keydown/keyup): у
 *    header-drawer.js keyup-слушатель Escape закрыл бы весь drawer
 *    вместе с листом (арх-консультация 2026-07-29).
 *
 * Выбор применяется стоковыми POST-формами 'localization' — здесь только
 * презентационное состояние (open/tab/query/info), ничего не сохраняем.
 *
 * PZ-434: панель приходит ПУСТОЙ. Её содержимое (все страны и языки,
 * snippets/pz-localization-panel.liquid) лежит один раз на страницу в
 * <template id="pz-loc-panel-template"> (layout/theme.liquid) и копируется
 * в панель при первом открытии (#fillPanel). Наполненность проверяем при
 * КАЖДОМ открытии, а не флагом: перерисовка шапки (добавление в корзину)
 * может вернуть пустую панель с сервера.
 */
const PANEL_TEMPLATE_ID = 'pz-loc-panel-template';
// Id форм в шаблоне кончаются на этот суффикс; меняем на вариант (pill/row),
// чтобы id двух наполненных панелей не совпали (дубль #LocalizationForm уже ловили).
const TEMPLATE_ID_SUFFIX = '-template';

if (!customElements.get('localization-selector')) {
  customElements.define(
    'localization-selector',
    class extends HTMLElement {
      #onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };

      connectedCallback() {
        this.isSheet = this.dataset.pzVariant === 'row';
        this.trigger = this.querySelector('[data-pz-loc-trigger]');
        this.panel = this.querySelector('[data-pz-loc-panel]');

        this.trigger.addEventListener('click', () => this.toggle());

        if (this.isSheet) {
          // Escape внутри листа не должен дойти до header-drawer
          for (const type of ['keydown', 'keyup']) {
            this.panel.addEventListener(type, (event) => {
              if (event.key === 'Escape') event.stopPropagation();
            });
          }
          this.panel.addEventListener('cancel', () => this.#syncClosed());
          this.panel.addEventListener('close', () => this.#syncClosed());
          // тап по ::backdrop = клик с target на самом dialog
          this.panel.addEventListener('click', (event) => {
            if (event.target === this.panel) this.close();
          });
        } else {
          this.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') this.close();
          });
          document.addEventListener('click', this.#onDocumentClick);
        }

        // ⓘ на row-триггере живёт вне панели — вешаем сразу; ⓘ панели — в #fillPanel
        this.querySelectorAll('[data-pz-info]').forEach((icon) => this.#bindInfoIcon(icon));
      }

      /* Копирует содержимое панели из шаблона, если панель пуста, и вешает
       * обработчики на его элементы. Идемпотентна: наполненную не трогает. */
      #fillPanel() {
        if (this.panel.querySelector('[data-pz-pane]')) return;
        const template = document.getElementById(PANEL_TEMPLATE_ID);
        if (!template) {
          // Шаблон рендерит layout/theme.liquid; без него панель останется пустой
          console.warn(`localization-selector: нет #${PANEL_TEMPLATE_ID}, панель пуста`);
          return;
        }

        const content = template.content.cloneNode(true);
        content.querySelectorAll(`[id$="${TEMPLATE_ID_SUFFIX}"]`).forEach((element) => {
          element.id = element.id.slice(0, -TEMPLATE_ID_SUFFIX.length) + '-' + this.dataset.pzVariant;
        });
        this.panel.append(content);
        this.#bindPanelControls();
      }

      /* Ссылки и обработчики на элементы свежескопированной панели. Старые
       * узлы (после перерисовки шапки) уходят вместе со своими обработчиками. */
      #bindPanelControls() {
        this.search = this.panel.querySelector('[data-pz-search]');
        this.empty = this.panel.querySelector('[data-pz-empty]');
        this.options = Array.from(this.panel.querySelectorAll('[data-pz-name]'));

        this.panel.querySelectorAll('[data-pz-tab]').forEach((tab) => {
          tab.addEventListener('click', () => this.selectTab(tab.dataset.pzTab));
        });
        this.panel.querySelectorAll('[data-pz-info]').forEach((icon) => this.#bindInfoIcon(icon));
        if (this.search) {
          this.search.addEventListener('input', () => this.filter(this.search.value));
        }
      }

      #bindInfoIcon(icon) {
        const toggle = (event) => {
          event.preventDefault();
          // ⓘ на row-триггере не должен открыть лист
          event.stopPropagation();
          const inPanel = icon.closest('[data-pz-loc-panel]');
          const text = inPanel
            ? inPanel.querySelector('[data-pz-info-text]')
            : this.querySelector('[data-pz-info-text-row]');
          if (!text) return;
          text.hidden = !text.hidden;
          icon.setAttribute('aria-expanded', text.hidden ? 'false' : 'true');
        };
        icon.addEventListener('click', toggle);
        icon.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') toggle(event);
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.#onDocumentClick);
      }

      get isOpen() {
        return this.isSheet ? this.panel.open : !this.panel.hidden;
      }

      toggle() {
        this.isOpen ? this.close() : this.open();
      }

      open() {
        this.#fillPanel();
        this.#refreshReturnTo();
        if (this.isSheet) {
          this.panel.showModal();
        } else {
          this.panel.hidden = false;
          // мобильный лист НЕ автофокусим (клавиатура закрыла бы его) —
          // фокус в поиск только в десктоп-выпадашке
          if (this.search) this.search.focus({ preventScroll: true });
        }
        this.trigger.setAttribute('aria-expanded', 'true');
      }

      /* return_to обязан указывать на страницу, где стоит покупатель.
       * В разметке Liquid его не знает: мобильный хедер приезжает через
       * Section Rendering API, и «текущий url» на сервере — это
       * «/?section_id=…» (белый экран после смены страны, баг
       * 2026-08-01). Разметка ставит безопасный request.path, точный
       * адрес подставляем здесь в момент открытия. section_id вычищаем
       * на случай, если панель открыта из отрендеренного секцией html. */
      #refreshReturnTo() {
        const params = new URLSearchParams(window.location.search);
        params.delete('section_id');
        const query = params.toString();
        const target = window.location.pathname + (query ? `?${query}` : '');
        this.querySelectorAll('input[name="return_to"]').forEach((input) => {
          input.value = target;
        });
      }

      close() {
        if (!this.isOpen) return;
        if (this.isSheet) {
          this.panel.close();
        } else {
          this.panel.hidden = true;
        }
        this.#syncClosed();
      }

      #syncClosed() {
        this.trigger.setAttribute('aria-expanded', 'false');
      }

      selectTab(name) {
        this.panel.querySelectorAll('[data-pz-tab]').forEach((tab) => {
          const active = tab.dataset.pzTab === name;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        this.panel.querySelectorAll('[data-pz-pane]').forEach((pane) => {
          pane.hidden = pane.dataset.pzPane !== name;
        });
        if (name === 'country' && this.search) {
          this.search.value = '';
          this.filter('');
        }
      }

      filter(value) {
        if (!this.options) return;
        const query = value.trim().toLowerCase();
        let shown = 0;
        this.options.forEach((option) => {
          const hit = !query || option.dataset.pzName.includes(query);
          option.parentElement.hidden = !hit;
          if (hit) shown += 1;
        });
        if (this.empty) this.empty.hidden = shown > 0;
      }
    }
  );
}
